import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import api from "../api";
import { useAuth } from "./AuthContext";
import { normalizeOrderQuantity } from "../utils/orderQuantity";

const ShopContext = createContext(null);

const MAX_RECENT = 20;
const CART_REQUEST_TIMEOUT_MS = 25000;

export function ShopProvider({ children }) {
  const { user } = useAuth();
  const [cartItems, setCartItems] = useState([]);
  const [cartUpdating, setCartUpdating] = useState(false);
  const [cartError, setCartError] = useState("");
  const cartItemsRef = useRef([]);
  const cartQueueRef = useRef(Promise.resolve());
  const cartVersionRef = useRef(0);
  const cartPendingRef = useRef(0);
  const cartErrorRef = useRef("");
  const userId = user?.id;
  const userIdRef = useRef(userId);
  userIdRef.current = userId;
  const cartCount = cartItems.length;

  const replaceCart = useCallback((items) => {
    cartItemsRef.current = items;
    setCartItems(items);
  }, []);
  const reportCartError = useCallback((message) => {
    cartErrorRef.current = message;
    setCartError(message);
  }, []);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [compareItems, setCompareItems] = useState(() => {
    const saved = localStorage.getItem("compareItems");
    return saved ? JSON.parse(saved) : [];
  });
  const [recentlyViewed, setRecentlyViewed] = useState(() => {
    const saved = localStorage.getItem("recentlyViewed");
    return saved ? JSON.parse(saved) : [];
  });

  // Persist compare & recently viewed to localStorage
  useEffect(() => {
    localStorage.setItem("compareItems", JSON.stringify(compareItems));
    // Log compare session when 2+ products
    if (compareItems.length >= 2) {
      const vid = localStorage.getItem("visitor_id") || "anon";
      api.post("/api/compare-log", {
        visitor_id: vid,
        user_id: user?.id || null,
        products: compareItems.map((p) => ({
          product_type: p._product_type,
          product_id: p._product_id,
          name: p.name || p.model_name || "",
        })),
      }).catch(() => {});
    }
  }, [compareItems, user]);

  useEffect(() => {
    localStorage.setItem("recentlyViewed", JSON.stringify(recentlyViewed));
  }, [recentlyViewed]);

  // Ignore reads started before a newer edit, or before the account changed.
  const fetchCart = useCallback(async ({ afterMutation = false } = {}) => {
    if (!userId) { replaceCart([]); return []; }
    const version = cartVersionRef.current;
    try {
      const { data } = await api.get("/api/cart", { timeout: CART_REQUEST_TIMEOUT_MS });
      if (userIdRef.current === userId && version === cartVersionRef.current
          && (cartPendingRef.current === 0 || afterMutation)) replaceCart(data);
      return data;
    } catch (err) {
      const error = err.response?.data?.error || "장바구니를 불러오지 못했습니다. 다시 시도해주세요.";
      if (userIdRef.current === userId && version === cartVersionRef.current) reportCartError(error);
      return { error };
    }
  }, [userId, replaceCart, reportCartError]);

  const fetchWishlist = useCallback(async () => {
    if (!user) { setWishlistItems([]); return; }
    try {
      const { data } = await api.get("/api/wishlist");
      setWishlistItems(data);
    } catch { /* silent */ }
  }, [user]);

  useEffect(() => {
    cartVersionRef.current += 1;
    replaceCart([]);
    reportCartError("");
    fetchCart({ afterMutation: true });
  }, [fetchCart, replaceCart, reportCartError]);
  useEffect(() => { fetchWishlist(); }, [fetchWishlist]);

  // Keep every click, but send mutations in order. The optimistic state is also
  // kept in a ref so two clicks before React renders cannot reuse an old count.
  const enqueueCartMutation = (request) => {
    const owner = userId;
    cartVersionRef.current += 1;
    cartPendingRef.current += 1;
    setCartUpdating(true);
    reportCartError("");
    const task = cartQueueRef.current.then(async () => {
      try {
        if (userIdRef.current !== owner) return { needLogin: true };
        const { data } = await request();
        return data;
      } catch (err) {
        const error = err.response?.data?.error || "장바구니 변경을 저장하지 못했습니다. 수량을 확인하고 다시 시도해주세요.";
        if (userIdRef.current === owner) reportCartError(error);
        return { error, needLogin: err.response?.status === 401 };
      } finally {
        // A single authoritative refresh after the last queued edit restores
        // rejected quantities and confirms per-unit shipping/totals as well.
        if (cartPendingRef.current === 1 && userIdRef.current === owner) await fetchCart({ afterMutation: true });
        cartPendingRef.current -= 1;
        if (cartPendingRef.current === 0) setCartUpdating(false);
      }
    });
    cartQueueRef.current = task;
    return task;
  };

  const syncCart = async () => {
    // An edit can start during either queue draining or the final GET.
    // Return only a snapshot that stayed current for the whole read.
    while (true) {
      const pending = cartQueueRef.current;
      await pending;
      if (pending !== cartQueueRef.current) continue;
      if (cartErrorRef.current) return { error: cartErrorRef.current };
      const version = cartVersionRef.current;
      const result = await fetchCart();
      if (pending === cartQueueRef.current && version === cartVersionRef.current) return result;
    }
  };

  const retryCart = async () => {
    await cartQueueRef.current;
    const result = await fetchCart();
    if (!result?.error) reportCartError("");
    return result;
  };

  const addToCart = async (productType, productId, quantity = 1) => {
    if (!user) return { needLogin: true };
    return enqueueCartMutation(() => api.post(
      "/api/cart",
      { product_type: productType, product_id: productId, quantity: normalizeOrderQuantity(quantity) },
      { timeout: CART_REQUEST_TIMEOUT_MS },
    ));
  };

  const updateCartQuantity = (itemId, quantity) => {
    if (!user) return Promise.resolve({ needLogin: true });
    const item = cartItemsRef.current.find(entry => entry.id === itemId);
    if (!item) return Promise.resolve({ error: "장바구니 상품을 찾을 수 없습니다." });
    const nextQuantity = normalizeOrderQuantity(typeof quantity === "function" ? quantity(item.quantity) : quantity);
    replaceCart(cartItemsRef.current.map(entry => entry.id === itemId ? {
      ...entry,
      quantity: nextQuantity,
      delivery_fee_total: Number(entry.delivery_fee_per_unit || 0) * nextQuantity,
    } : entry));
    return enqueueCartMutation(() => api.put(`/api/cart/${itemId}`,
      { quantity: nextQuantity }, { timeout: CART_REQUEST_TIMEOUT_MS }));
  };

  const removeFromCart = (itemId) => {
    if (!user) return Promise.resolve({ needLogin: true });
    replaceCart(cartItemsRef.current.filter(entry => entry.id !== itemId));
    return enqueueCartMutation(() => api.delete(`/api/cart/${itemId}`, { timeout: CART_REQUEST_TIMEOUT_MS }));
  };

  // Wishlist actions
  const toggleWishlist = async (productType, productId) => {
    if (!user) return { needLogin: true };
    try {
      const { data } = await api.post("/api/wishlist", { product_type: productType, product_id: productId });
      fetchWishlist();
      return data;
    } catch (err) {
      return { error: err.response?.data?.error || "오류가 발생했습니다" };
    }
  };

  const removeFromWishlist = async (itemId) => {
    try {
      await api.delete(`/api/wishlist/${itemId}`);
      fetchWishlist();
    } catch { /* silent */ }
  };

  const isWishlisted = (productType, productId) => {
    return wishlistItems.some(
      (item) => item.product_type === productType && item.product_id === productId
    );
  };

  // Compare actions (localStorage-based, no login required)
  const addToCompare = (product) => {
    setCompareItems((prev) => {
      if (prev.find((p) => p._product_type === product._product_type && p._product_id === product._product_id)) return prev;
      return [...prev, product];
    });
  };

  const removeFromCompare = (productType, productId) => {
    setCompareItems((prev) => prev.filter(
      (p) => !(p._product_type === productType && p._product_id === productId)
    ));
  };

  const isInCompare = (productType, productId) => {
    return compareItems.some(
      (p) => p._product_type === productType && p._product_id === productId
    );
  };

  const clearCompare = () => setCompareItems([]);

  // Recently viewed
  const addToRecentlyViewed = (product) => {
    setRecentlyViewed((prev) => {
      const filtered = prev.filter(
        (p) => !(p.product_type === product.product_type && p.product_id === product.product_id)
      );
      return [product, ...filtered].slice(0, MAX_RECENT);
    });
  };

  return (
    <ShopContext.Provider value={{
      cartItems, cartCount, cartUpdating, cartError, addToCart, updateCartQuantity, removeFromCart, fetchCart, syncCart, retryCart,
      wishlistItems, toggleWishlist, removeFromWishlist, isWishlisted, fetchWishlist,
      compareItems, addToCompare, removeFromCompare, isInCompare, clearCompare, replaceCompareItems: setCompareItems,
      recentlyViewed, addToRecentlyViewed,
    }}>
      {children}
    </ShopContext.Provider>
  );
}

export function useShop() {
  const ctx = useContext(ShopContext);
  if (!ctx) throw new Error("useShop must be used within ShopProvider");
  return ctx;
}
